import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Facebook, Linkedin, Send, Twitter, X } from 'lucide-react';

const SocialIcon = ({ href, icon: Icon, colorClass, glowClass }) => (
  <motion.a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className={`p-2 rounded-full ${colorClass} text-white transition-all duration-300`}
    whileHover={{
      scale: 1.2,
      y: -2,
      boxShadow: `0 0 15px var(${glowClass})`,
      filter: `drop-shadow(0 0 8px var(${glowClass}))`,
    }}
  >
    <Icon size={20} />
  </motion.a>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [isModalOpen, setModalOpen] = useState(false);

  // 所有链接
  const footerLinks = [
    { href: '#', label: 'About us', action: () => setModalOpen(true) },
    { href: 'Privacy Policy.html', label: 'Privacy Policy' },
    { href: 'Advertiser.html', label: 'Advertiser Terms and Conditions ' },
     { href: 'Publisher.html', label: 'Publisher Terms and Conditions ' },
    { href: 'Cookie Policy.html', label: 'Cookie Policy' },
  ];

  // Quick Links 只显示 About us
  const quickLinks = footerLinks.filter(link => link.label === 'About us');

  // Legal 显示其余所有政策
  const legalLinks = footerLinks.filter(link => link.label !== 'About us');

  const socialIcons = [
    { href: 'https://facebook.com', icon: Facebook, colorClass: 'bg-blue-600', glowClass: '--neon-blue' },
    { href: 'https://twitter.com', icon: Twitter, colorClass: 'bg-sky-500', glowClass: '--neon-blue' },
    { href: 'https://telegram.org', icon: Send, colorClass: 'bg-sky-400', glowClass: '--neon-blue' },
    { href: 'https://linkedin.com', icon: Linkedin, colorClass: 'bg-blue-700', glowClass: '--neon-blue' },
  ];

  return (
    <>
      <footer className="glassmorphism border-t border-neon-blue/30 text-cyber-text-secondary py-12 mt-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
            {/* Logo & 联系方式 */}
            <div>
              <div className="flex items-center mb-4">
                <img
                  src="/logo.png"
                  alt="Galaxioid Logo"
                  className="h-12 w-12 mr-2 animate-pulse select-none pointer-events-none"
                  style={{
                    filter:
                      'drop-shadow(0 0 4px rgba(255,255,255,0.85)) drop-shadow(0 0 8px rgba(0,255,255,0.8)) drop-shadow(0 0 12px rgba(0,255,255,0.65))',
                  }}
                />
                <span className="text-2xl font-bold text-cyber-text-primary text-glow-blue">
                  Galaxioid
                </span>
              </div>
              <p className="text-sm">Connecting Advertisers to Global Traffic.</p>
              <p className="text-sm mt-2">Harju maakond, Tallinn, Kesklinna linnaosa, Sakala tn 7-2, 10141 </p>
              <p className="text-sm">Email: contact@galaxioid.net</p>
            </div>

            {/* Quick Links */}
            <div>
              <p className="font-semibold text-cyber-text-primary mb-4 text-glow-pink">Quick Links</p>
              <ul className="space-y-2">
                {quickLinks.map(link => (
                  <li key={link.label}>
                    <motion.a
                      href={link.href}
                      onClick={e => {
                        e.preventDefault();
                        link.action();
                      }}
                      className="text-sm hover:text-neon-pink hover:text-glow-pink transition-colors cursor-pointer"
                      whileHover={{ x: 2, textShadow: '0 0 5px var(--neon-pink)' }}
                    >
                      {link.label}
                    </motion.a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Legal */}
            <div>
              <p className="font-semibold text-cyber-text-primary mb-4 text-glow-purple">Legal</p>
              <ul className="space-y-2">
                {legalLinks.map(link => (
                  <li key={link.href}>
                    <motion.a
                      href={link.href}
                      className="text-sm hover:text-neon-purple hover:text-glow-purple transition-colors"
                      whileHover={{ x: 2, textShadow: '0 0 5px var(--neon-purple)' }}
                    >
                      {link.label}
                    </motion.a>
                  </li>
                ))}
              </ul>
            </div>

            {/* 社交图标 */}
            <div>
              <p className="font-semibold text-cyber-text-primary mb-4 text-glow-blue">Follow Us</p>
              <div className="flex space-x-3">
                {socialIcons.map(social => (
                  <SocialIcon key={social.href} {...social} />
                ))}
              </div>
            </div>
          </div>

          <div className="border-t border-neon-purple/30 pt-8 text-center">
            <p className="text-sm">
              Copyright &copy; {currentYear} Galaxioid. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* About Us Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50"
          onClick={() => setModalOpen(false)}
        >
          <div
            className="bg-gray-900 text-white p-6 rounded-lg w-11/12 max-w-md relative"
            onClick={e => e.stopPropagation()}
          >
            <button
              className="absolute top-3 right-3 text-gray-400 hover:text-white"
              onClick={() => setModalOpen(false)}
            >
              <X size={20} />
            </button>
            <h2 className="text-xl font-semibold mb-4">About Galaxioid</h2>
            <p className="text-sm leading-relaxed">
              Galaxioid is dedicated to connecting advertisers with premium global traffic.
              Our mission is to deliver cutting-edge advertising solutions that maximize ROI
              and foster long-term partnerships. With a focus on innovation and transparency,
              we help brands reach their audience anywhere, anytime.
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default Footer;